import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Pencil, Trash2, MapPin, Package, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { StockItem, LocationQuantity } from "@/types/stock-item";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { useWarehousesLocations } from "@/hooks/use-warehouses-locations";
import { QuickTransferModal } from "@/components/quick-transfer-modal";

interface StockItemLocationsTabProps {
  item: StockItem;
  isEditMode: boolean;
  onItemChange?: (item: StockItem) => void;
  isLiveMode?: boolean;
}

export function StockItemLocationsTab({
  item,
  isEditMode,
  onItemChange,
  isLiveMode = true,
}: StockItemLocationsTabProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [quantity, setQuantity] = useState("");
  const [allocated, setAllocated] = useState("");
  const [dueIn, setDueIn] = useState("");
  const [isQuickTransferOpen, setIsQuickTransferOpen] = useState(false);

  // Backend data hook
  const {
    warehouses: backendWarehouses,
    locations: backendLocations,
    fetchWarehouses,
    fetchLocations,
  } = useWarehousesLocations();

  // Get data source based on mode
  const warehouses = useMemo(() => {
    return isLiveMode ? backendWarehouses : mockWarehouses;
  }, [isLiveMode, backendWarehouses]);

  const locations = useMemo(() => {
    return isLiveMode ? backendLocations : mockLocations;
  }, [isLiveMode, backendLocations]);

  // Get location quantities (initialize if not present)
  const locationQuantities = useMemo(() => {
    if (item.locationQuantities && item.locationQuantities.length > 0) {
      return item.locationQuantities;
    }
    // Initialize with current warehouse/location if available
    if (item.warehouseCode && item.locationCode && item.physicalQuantity > 0) {
      return [
        {
          warehouseCode: item.warehouseCode,
          locationCode: item.locationCode,
          quantity: item.physicalQuantity,
        },
      ];
    }
    return [];
  }, [item]);

  // Calculate total quantity
  const totalQuantity = useMemo(() => {
    return locationQuantities.reduce((sum, loc) => sum + loc.quantity, 0);
  }, [locationQuantities]);

  // Get warehouse name
  const getWarehouseName = (code: string) => {
    const warehouse = warehouses.find((w) => w.code === code);
    return warehouse?.name || code;
  };

  // Get location name
  const getLocationName = (code: string) => {
    const location = locations.find((l) => l.locationCode === code);
    return location?.locationCode || code;
  };

  // Filter locations by selected warehouse
  const availableLocations = useMemo(() => {
    if (!selectedWarehouse) return [];
    return locations.filter((loc) => loc.warehouseCode === selectedWarehouse && loc.active);
  }, [locations, selectedWarehouse]);

  // Handle add location
  const handleAddLocation = () => {
    if (!selectedWarehouse || !selectedLocation || !quantity) {
      toast.error("Please fill in all fields");
      return;
    }

    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      toast.error("Quantity must be a positive number");
      return;
    }

    // Check if location already exists
    const existingIndex = locationQuantities.findIndex(
      (loc) => loc.warehouseCode === selectedWarehouse && loc.locationCode === selectedLocation
    );

    if (existingIndex !== -1) {
      toast.error("This location already exists. Please edit the existing entry.");
      return;
    }

    const allocatedNum = allocated ? parseFloat(allocated) : 0;
    const dueInNum = dueIn ? parseFloat(dueIn) : 0;

    const newLocationQuantity: LocationQuantity = {
      warehouseCode: selectedWarehouse,
      locationCode: selectedLocation,
      quantity: quantityNum,
      allocated: allocatedNum,
      dueIn: dueInNum,
      warehouseName: getWarehouseName(selectedWarehouse),
      locationName: getLocationName(selectedLocation),
    };

    const updatedLocationQuantities = [...locationQuantities, newLocationQuantity];
    const newTotalQuantity = updatedLocationQuantities.reduce((sum, loc) => sum + loc.quantity, 0);

    // Update item
    const updatedItem: StockItem = {
      ...item,
      locationQuantities: updatedLocationQuantities,
      physicalQuantity: newTotalQuantity,
      // Update primary location to first location if not set
      warehouseCode: item.warehouseCode || selectedWarehouse,
      locationCode: item.locationCode || selectedLocation,
    };

    onItemChange?.(updatedItem);
    toast.success("Location added successfully");
    resetDialog();
  };

  // Handle edit location
  const handleEditLocation = () => {
    if (editingIndex === null) return;

    if (!selectedWarehouse || !selectedLocation || !quantity) {
      toast.error("Please fill in all fields");
      return;
    }

    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      toast.error("Quantity must be a positive number");
      return;
    }

    // Check if location already exists (excluding current)
    const existingIndex = locationQuantities.findIndex(
      (loc, idx) =>
        idx !== editingIndex &&
        loc.warehouseCode === selectedWarehouse &&
        loc.locationCode === selectedLocation
    );

    if (existingIndex !== -1) {
      toast.error("This location already exists. Please choose a different location.");
      return;
    }

    const allocatedNum = allocated ? parseFloat(allocated) : 0;
    const dueInNum = dueIn ? parseFloat(dueIn) : 0;

    const updatedLocationQuantities = [...locationQuantities];
    updatedLocationQuantities[editingIndex] = {
      warehouseCode: selectedWarehouse,
      locationCode: selectedLocation,
      quantity: quantityNum,
      allocated: allocatedNum,
      dueIn: dueInNum,
      warehouseName: getWarehouseName(selectedWarehouse),
      locationName: getLocationName(selectedLocation),
    };

    const newTotalQuantity = updatedLocationQuantities.reduce((sum, loc) => sum + loc.quantity, 0);

    const updatedItem: StockItem = {
      ...item,
      locationQuantities: updatedLocationQuantities,
      physicalQuantity: newTotalQuantity,
    };

    onItemChange?.(updatedItem);
    toast.success("Location updated successfully");
    resetDialog();
  };

  // Handle delete location
  const handleDeleteLocation = (index: number) => {
    const updatedLocationQuantities = locationQuantities.filter((_, idx) => idx !== index);
    const newTotalQuantity = updatedLocationQuantities.reduce((sum, loc) => sum + loc.quantity, 0);

    const updatedItem: StockItem = {
      ...item,
      locationQuantities: updatedLocationQuantities,
      physicalQuantity: newTotalQuantity,
      // Update primary location if we deleted it
      warehouseCode: updatedLocationQuantities[0]?.warehouseCode || "",
      locationCode: updatedLocationQuantities[0]?.locationCode || "",
    };

    onItemChange?.(updatedItem);
    toast.success("Location removed successfully");
  };

  // Open edit dialog
  const openEditDialog = (index: number) => {
    const location = locationQuantities[index];
    setEditingIndex(index);
    setSelectedWarehouse(location.warehouseCode);
    setSelectedLocation(location.locationCode);
    setQuantity(location.quantity.toString());
    setAllocated((location.allocated || 0).toString());
    setDueIn((location.dueIn || 0).toString());
    setIsEditDialogOpen(true);
  };

  // Reset dialog
  const resetDialog = () => {
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setEditingIndex(null);
    setSelectedWarehouse("");
    setSelectedLocation("");
    setQuantity("");
    setAllocated("");
    setDueIn("");
  };

  return (
    <div className="space-y-6">
      {/* Summary Card - Single card with location and quantity info */}
      <Card>
        <CardHeader>
          <CardTitle>Location Summary</CardTitle>
          <CardDescription>Stock distribution across locations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">Total Locations</span>
              </div>
              <p className="text-2xl font-bold">{locationQuantities.length}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Package className="h-4 w-4" />
                <span className="text-sm">Total Quantity</span>
              </div>
              <p className="text-2xl font-bold">{totalQuantity.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Locations Table */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Distribution
          </h3>
          <div className="flex items-center gap-2">
            {locationQuantities.length > 0 && (
              <Button
                onClick={() => setIsQuickTransferOpen(true)}
                size="sm"
                variant="secondary"
              >
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Quick Transfer
              </Button>
            )}
            {isEditMode && (
              <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Location
              </Button>
            )}
          </div>
        </div>

        {locationQuantities.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-2">No locations assigned</p>
              {isEditMode && (
                <Button onClick={() => setIsAddDialogOpen(true)} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Location
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Physical Qty</TableHead>
                  <TableHead className="text-right">Allocated</TableHead>
                  <TableHead className="text-right">Unallocated</TableHead>
                  <TableHead className="text-right">Free Qty</TableHead>
                  <TableHead className="text-right">Due In</TableHead>
                  <TableHead className="text-right">% of Total</TableHead>
                  {isEditMode && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {locationQuantities.map((loc, index) => {
                  const percentage = totalQuantity > 0 ? (loc.quantity / totalQuantity) * 100 : 0;
                  const isPrimary = loc.warehouseCode === item.warehouseCode && loc.locationCode === item.locationCode;
                  
                  // Calculate quantities
                  const physicalQty = loc.quantity || 0;
                  const allocated = loc.allocated || 0;
                  const unallocated = physicalQty - allocated;
                  const dueIn = loc.dueIn || 0;
                  const freeQty = unallocated + dueIn;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getWarehouseName(loc.warehouseCode)}
                          {isPrimary && (
                            <Badge variant="secondary" className="text-xs">
                              Primary
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {getLocationName(loc.locationCode)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {physicalQty.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {allocated > 0 ? (
                          <span className="text-orange-600">{allocated.toLocaleString()}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {unallocated.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {freeQty > 0 ? (
                          <span className="text-green-600">{freeQty.toLocaleString()}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {dueIn > 0 ? (
                          <span className="text-blue-600">{dueIn.toLocaleString()}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {percentage.toFixed(1)}%
                      </TableCell>
                      {isEditMode && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(index)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLocation(index)}
                              disabled={locationQuantities.length === 1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {/* Totals Row */}
                <TableRow className="bg-muted/50 font-semibold border-t-2">
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right">
                    {locationQuantities.reduce((sum, loc) => sum + (loc.quantity || 0), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {locationQuantities.reduce((sum, loc) => sum + (loc.allocated || 0), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {locationQuantities.reduce((sum, loc) => sum + ((loc.quantity || 0) - (loc.allocated || 0)), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {locationQuantities.reduce((sum, loc) => sum + ((loc.quantity || 0) - (loc.allocated || 0) + (loc.dueIn || 0)), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {locationQuantities.reduce((sum, loc) => sum + (loc.dueIn || 0), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">100%</TableCell>
                  {isEditMode && <TableCell />}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add Location Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Location</DialogTitle>
            <DialogDescription>
              Add a new location for this stock item
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="warehouse">Warehouse</Label>
              <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                <SelectTrigger id="warehouse">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses
                    .filter((w) => w.active)
                    .map((warehouse) => (
                      <SelectItem key={warehouse.code} value={warehouse.code}>
                        {warehouse.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={selectedLocation}
                onValueChange={setSelectedLocation}
                disabled={!selectedWarehouse}
              >
                <SelectTrigger id="location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {availableLocations.map((location) => (
                    <SelectItem key={location.locationCode} value={location.locationCode}>
                      {location.locationCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Physical Quantity</Label>
              <Input
                id="quantity"
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Enter physical quantity"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="allocated">Allocated</Label>
                <Input
                  id="allocated"
                  type="number"
                  min="0"
                  step="1"
                  value={allocated}
                  onChange={(e) => setAllocated(e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueIn">Due In</Label>
                <Input
                  id="dueIn"
                  type="number"
                  min="0"
                  step="1"
                  value={dueIn}
                  onChange={(e) => setDueIn(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>
              Cancel
            </Button>
            <Button onClick={handleAddLocation}>Add Location</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
            <DialogDescription>
              Update the location details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-warehouse">Warehouse</Label>
              <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                <SelectTrigger id="edit-warehouse">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses
                    .filter((w) => w.active)
                    .map((warehouse) => (
                      <SelectItem key={warehouse.code} value={warehouse.code}>
                        {warehouse.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-location">Location</Label>
              <Select
                value={selectedLocation}
                onValueChange={setSelectedLocation}
                disabled={!selectedWarehouse}
              >
                <SelectTrigger id="edit-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {availableLocations.map((location) => (
                    <SelectItem key={location.locationCode} value={location.locationCode}>
                      {location.locationCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-quantity">Physical Quantity</Label>
              <Input
                id="edit-quantity"
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Enter physical quantity"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-allocated">Allocated</Label>
                <Input
                  id="edit-allocated"
                  type="number"
                  min="0"
                  step="1"
                  value={allocated}
                  onChange={(e) => setAllocated(e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-dueIn">Due In</Label>
                <Input
                  id="edit-dueIn"
                  type="number"
                  min="0"
                  step="1"
                  value={dueIn}
                  onChange={(e) => setDueIn(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>
              Cancel
            </Button>
            <Button onClick={handleEditLocation}>Update Location</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Transfer Modal */}
      <QuickTransferModal
        open={isQuickTransferOpen}
        onOpenChange={setIsQuickTransferOpen}
        item={item}
        isLiveMode={isLiveMode}
        onTransferComplete={() => {
          // Refresh the locations data if needed
          toast.success("Transfer completed successfully");
        }}
      />
    </div>
  );
}
